// src/components/admin/UsageStatistics.tsx
import React, { useEffect, useState } from 'react';
import { getAdminStatistics } from '../../services/adminService';
import { Users, Calculator, FileText, CreditCard, DollarSign, TrendingUp } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';

interface Statistics {
  totalUsers: number;
  quickEstimates: number;
  detailedEstimates: number;
  totalCredits: number;
  revenue: {
    total: number;
    byYear: Record<string, number>;
  };
  activeUsers: {
    daily: number;
    weekly: number;
    monthly: number;
  };
}

const UsageStatistics = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
  try {
    setLoading(true);
    setError(null);
    const data = await getAdminStatistics();
    setStatistics(data);
  } catch (err) {
    console.error('Erreur lors du chargement des statistiques:', err);
    setError(err instanceof Error ? err.message : 'Impossible de charger les statistiques');
  } finally {
    setLoading(false);
  }
};

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!statistics) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          icon={<Users className="h-8 w-8 text-blue-500" />}
          title="Utilisateurs inscrits"
          value={statistics.totalUsers}
          trend={statistics.activeUsers}
          trendLabels={{
            daily: 'Aujourd\'hui',
            weekly: 'Cette semaine',
            monthly: 'Ce mois'
          }}
        />
        
        <StatCard
          icon={<Calculator className="h-8 w-8 text-green-500" />}
          title="Estimations rapides"
          value={statistics.quickEstimates}
        />
        
        <StatCard
          icon={<FileText className="h-8 w-8 text-purple-500" />}
          title="Estimations détaillées"
          value={statistics.detailedEstimates}
        />
        
        <StatCard
          icon={<CreditCard className="h-8 w-8 text-orange-500" />}
          title="Crédits vendus"
          value={statistics.totalCredits}
        />

        <StatCard
          icon={<CreditCard className="h-8 w-8 text-orange-500" />}
          title="Crédits par transaction"
          value={(statistics.totalCredits / (statistics.revenue.transactions || 1)).toFixed(1)}
        />
        
        <StatCard
          icon={<DollarSign className="h-8 w-8 text-emerald-500" />}
          title="Chiffre d'affaires total"
          value={formatCurrency(statistics.revenue.total)}
        />
        
        <StatCard
          icon={<TrendingUp className="h-8 w-8 text-indigo-500" />}
          title="Croissance mensuelle"
          value="+12%" // À calculer en fonction des données réelles
        />
      </div>

      {/* Chiffre d'affaires par année */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Chiffre d'affaires par année</h3>
        <div className="space-y-4">
          {Object.entries(statistics.revenue.byYear)
            .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
            .map(([year, amount]) => (
              <div key={year} className="flex items-center justify-between">
                <span className="font-medium">{year}</span>
                <span className="text-primary">{formatCurrency(amount)}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  trend?: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  trendLabels?: {
    daily: string;
    weekly: string;
    monthly: string;
  };
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, trend, trendLabels }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
      <span className="text-2xl font-bold">{value}</span>
    </div>
    <h3 className="font-medium text-gray-700">{title}</h3>
    
    {trend && trendLabels && (
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>{trendLabels.daily}</span>
          <span>{trend.daily}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>{trendLabels.weekly}</span>
          <span>{trend.weekly}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>{trendLabels.monthly}</span>
          <span>{trend.monthly}</span>
        </div>
      </div>
    )}
  </div>
);

export default UsageStatistics;
